/** "全部" 分类常量 */
export const ALL_CATEGORY = 'all';



/**
 * Skill 来源类型枚举（source_type）
 */
export enum ESkillSourceType {
  /** 官方 */
  OFFICIAL = 1,
  /** 个人 */
  PERSONAL = 2,
}

/**
 * Skill 安全状态枚举（security_status）
 */
export enum ESkillSecurityStatus {
  /** 不安全 */
  UNSAFE = 0,
  /** 安全 */
  SAFE = 1,
  /** 审核中 */
  UNDER_REVIEW = 2,
}

/**
 * Skill 操作类型枚举（action_type）
 */
export enum ESkillActionType {
  /** 安装到 DataBrain */
  INSTALL = 1,
  /** 下载到本地 */
  DOWNLOAD = 2,
}

/**
 * Skill 包环境版本（download 接口 version）
 * 未传时后端默认 woa；Global / Service 的中英文 MD 仍读 zh_md / en_md
 */
export type TSkillPackageVersion = 'woa' | 'global' | 'service';

/**
 * 分类 TAB 配置项（动态生成）
 */
export interface ICategoryTab {
  /** 分类唯一标识（接口返回的 category_id，或 'all'） */
  key: string;
  /** 显示文案 */
  label: string;
  /** 分类图标 */
  icon?: string;
}

/**
 * Skill 分类数据结构（/api/v1/util/skills/categories/list）
 */
export interface ISkillCategoryData {
  /** 分类唯一 ID（如 category_data_analysis） */
  category_id: string;
  /** 分类中文名 */
  category: string;
  /** 分类英文名 */
  category_en?: string;
  /** 分类图标 */
  category_icon?: string;
  /** 分类文字颜色 */
  category_text_color?: string;
  /** 分类文字背景色 */
  category_text_color_background?: string;
  /** 排序 */
  sort?: number;
  /** 创建时间 */
  create_time?: string;
  /** 更新时间 */
  update_time?: string;
}

/**
 * Skill 分类列表响应数据
 */
export interface IGetSkillCategoriesListResponse {
  /** 总记录数 */
  total: number;
  /** 当前页码 */
  page: number;
  /** 每页条数 */
  page_size: number;
  /** 分类列表 */
  list: ISkillCategoryData[];
}

/**
 * Skill 数据结构（与接口返回字段对应）
 */

export interface ISkillData {
  /** Skill 唯一标识 */
  skill_id: string;
  /** Skill 名称（英文），与技能包文件名一致（不含 .zip 后缀）；用于 API / 文件名校验等 */
  skill_name: string;
  /** Skill 名称（中文，兼容旧接口） */
  skill_name_cn?: string;
  /** 展示名（中文），有值时优先于 skill_name_cn / skill_name 用于 UI */
  display_name_cn?: string;
  /** 展示名（英文），有值时优先于 skill_name 用于 UI */
  display_name_en?: string;
  /** 分类唯一 ID（新接口字段，可能为空） */
  category_id?: string;
  /** 分类名称（中文） */
  category: string;

  /** 分类名称（英文） */
  category_en?: string;
  /** 分类文字颜色（如 #029CD4） */
  category_text_color?: string;
  /** 分类背景颜色（如 #D6EFF8） */
  category_text_color_background?: string;

  /** 分类图标 */
  category_icon: string;

  /** 描述（中文） */
  description: string;
  /** 描述（英文） */
  description_en: string;
  /** 精简描述（中文，用于下拉选项） */
  shortDescription_cn?: string;
  /** 精简描述（英文，用于下拉选项） */
  shortDescription?: string;
  /** 来源类型：1 官方，2 个人 */
  source_type: ESkillSourceType;
  /** 来源标签（中文） */
  source_label: string;
  /** 来源标签（英文） */
  source_label_en: string;
  /** 上传者 ID，官方 Skill 可能为空 */
  uploader_id: string;
  /** 上传者名称 */
  user_name: string;
  /** 作者展示名（有则底栏、无障碍文案等优先于 user_name） */
  author_name?: string;
  /** 上传者头像 */
  profile_pic: string;
  /** 作者头像（有值时优先于 profile_pic 及官方默认图） */
  author_avatar?: string;
  /**
   * Skill 包 COS 路径。
   * list 传入 version 后由后端按环境映射，统一填此字段（WOA=file_url / Global=global_file_url / Service=service_file_url）。
   * 前端当前不消费该字段。
   */
  file_url: string;
  /** 中文正文（zip 包内 skill.md 原文，含 frontmatter，可能为空） */
  zh_md?: string;
  /** 英文正文（产品上传 .md 文件内容，可能为空） */
  en_md?: string;
  /** 英文正文上传文件名（en_md 非空时有值） */
  en_md_file_name?: string;
  /** 安全状态：1 安全，0 不安全，2 审核中 */
  security_status: ESkillSecurityStatus;
  /** 安全状态描述 */
  security_label: string;
  /** 当前用户是否已安装到 DataBrain */
  is_installed: boolean;
  /** 当前用户是否已下载到本地 */
  is_downloaded: boolean;
  /** 是否支持下载：1=支持，0=不支持 */
  downloadable?: 0 | 1;
  /** 是否支持对话：1=支持，0=不支持；缺省按 1 */
  dialogable?: 0 | 1;
  /** 对话次数（使用量） */
  dialog_count?: number;
  /** DIY 中文例句 */
  diy_example?: string;
  /** DIY 英文例句 */
  diy_example_en?: string;
  /** Knot 链接地址 */
  knot_link?: string;
  /** 是否展示 Knot 按钮：1 展示，0 不展示 */
  show_knot_btn?: 0 | 1;
  /** 排序值 */
  sort?: number;
  /** 创建时间 */
  create_time: string;
  /** 更新时间 */
  update_time: string;
}

/**
 * 获取 Skills 列表请求参数
 */
export interface IGetSkillsListParams {
  /** 分类筛选：all、data_analysis、public_opinion、other */
  category?: string;
  /** 关键词搜索由前端按语言：中文匹配 display_name_cn + description，英文匹配 display_name_en + description_en，不匹配 skill_name */
  keyword?: string;
  /** 页码，从 1 开始 */
  page?: number;
  /** 每页条数，最大 100 */
  page_size?: number;
  /**
   * 包环境：woa / global / service。
   * 后端据此映射后把对应包地址写进返回的 file_url。
   */
  version?: TSkillPackageVersion;
}

/**
 * 获取 Skills 列表响应数据
 */
export interface IGetSkillsListResponse {
  /** 总记录数 */
  total: number;
  /** 当前页码 */
  page: number;
  /** 每页条数 */
  page_size: number;
  /** Skills 列表 */
  list: ISkillData[];
}

/**
 * 记录下载操作请求参数
 */
export interface IRecordDownloadParams {
  /** Skill 唯一标识 */
  skill_id: string;
  /** 包版本：woa / global / service，未传后端默认 woa */
  version?: TSkillPackageVersion;
  /** 操作类型：1 安装到 DataBrain，2 下载到本地 */
  action_type: ESkillActionType;
  /** 客户端信息 */
  client_info?: string;
}

/**
 * 记录下载操作响应数据
 */
export interface IRecordDownloadResponse {
  /** Skill 唯一标识 */
  skill_id: string;
  /** Skill 名称 */
  skill_name: string;
  /** 操作类型：1 安装，2 下载 */
  action_type: ESkillActionType;
  /** 操作结果描述 */
  action_label: string;
  /** 是否已安装，仅在安装场景下为 true */
  is_installed: boolean;
  /** 操作时间 */
  create_time: string;
}

export * from './skillDetail';
